// File: frontend/src/pages/Socrates/contexts/AIAgentsContext.js

import React, { createContext, useContext, useState, useCallback } from 'react';

const AIAgentsContext = createContext();

export const useAIAgents = () => {
  const context = useContext(AIAgentsContext);
  if (!context) {
    throw new Error('useAIAgents must be used within an AIAgentsProvider');
  }
  return context;
};

export const AIAgentsProvider = ({ children }) => {
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Mock agent data for now
  const mockAgents = [
    {
      plato_agent_name: 'customer-service-agent',
      display_name: 'Customer Service Agent',
      description: 'Handles customer inquiries and support requests',
      status: 'active',
      created_at: '2024-01-15T10:30:00Z',
      updated_at: '2024-01-20T14:45:00Z',
      artifacts_count: 12,
      variables_count: 8,
      permissions: ['read', 'write']
    },
    {
      plato_agent_name: 'data-analysis-agent',
      display_name: 'Data Analysis Agent',
      description: 'Processes and analyzes business data',
      status: 'active',
      created_at: '2024-01-10T09:15:00Z',
      updated_at: '2024-01-18T16:20:00Z',
      artifacts_count: 25,
      variables_count: 15,
      permissions: ['read', 'write', 'admin']
    }
  ];

  // Load agents
  const loadAgents = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 500));
      setAgents(mockAgents);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Get agent by name
  const getAgentByName = useCallback((agentName) => {
    return agents.find(agent => agent.plato_agent_name === agentName);
  }, [agents]);

  // Select agent
  const selectAgent = useCallback((agentName) => {
    const agent = getAgentByName(agentName);
    setSelectedAgent(agent);
  }, [getAgentByName]);

  // Clear selected agent
  const clearSelectedAgent = useCallback(() => {
    setSelectedAgent(null);
  }, []);

  // Create new agent
  const createAgent = useCallback(async (agentData) => {
    setLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const newAgent = {
        ...agentData,
        plato_agent_name: agentData.plato_agent_name || agentData.display_name.toLowerCase().replace(/\s+/g, '-'),
        status: 'active',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
        artifacts_count: 0,
        variables_count: 0,
        permissions: ['read']
      };

      setAgents(prev => [...prev, newAgent]);
      return newAgent;
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  // Update agent
  const updateAgent = useCallback(async (agentName, updates) => {
    setLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 800));
      
      setAgents(prev => prev.map(agent => 
        agent.plato_agent_name === agentName 
          ? { ...agent, ...updates, updated_at: new Date().toISOString() }
          : agent
      ));

      if (selectedAgent?.plato_agent_name === agentName) {
        setSelectedAgent(prev => ({ ...prev, ...updates, updated_at: new Date().toISOString() }));
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedAgent]);

  // Delete agent
  const deleteAgent = useCallback(async (agentName) => {
    setLoading(true);
    setError(null);

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setAgents(prev => prev.filter(agent => agent.plato_agent_name !== agentName));
      
      if (selectedAgent?.plato_agent_name === agentName) {
        setSelectedAgent(null);
      }
    } catch (err) {
      setError(err.message);
      throw err;
    } finally {
      setLoading(false);
    }
  }, [selectedAgent]);

  const value = {
    // State
    agents,
    selectedAgent,
    loading,
    error,

    // Actions
    loadAgents,
    getAgentByName,
    selectAgent,
    clearSelectedAgent,
    createAgent,
    updateAgent,
    deleteAgent,
    setError
  };

  return (
    <AIAgentsContext.Provider value={value}>
      {children}
    </AIAgentsContext.Provider>
  );
};